import React, { useEffect } from "react";
import {
  FocusContext,
  useFocusable,
} from "@noriginmedia/norigin-spatial-navigation";
import styled from "styled-components";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectDisableNavigation } from "../../redux/Slices/loading.slice";

/**
 * Sidebar Component
 * 
 * Legacy CSS: NO flexbox
 * Uses: display:table, table-cell, inline-block
 */

// Menu item box with icon support
var MenuItemBox = styled.div`
  width: 100%;
  height: 63px;
  background-color: ${function (props) {
    if (props.focused) return "#85000080";
    if (props.isActive) return "rgba(255, 255, 255, 0.08)";
    return "transparent";
  }};
  border: none;
  box-sizing: border-box;
  border-radius: 0px;
  margin-bottom: 7px;
  transition: all ease 0.2s;
  color: #fff;
  font-weight: 700;
  font-size: 22px;
  padding: 0 1.5rem;
  position: relative;
  cursor: pointer;
  text-align: right;
  font-family: "AnjomanMax", sans-serif;
  display: table;
  table-layout: fixed;

  &:after {
    content: "";
    position: absolute;
    right: 0;
    top: 5px;
    bottom: 5px;
    width: 15px;
    background-color: ${function (props) {
    if (props.focused) return "rgba(180, 28, 32, 0.9)";
    if (props.isActive) return "rgba(180, 28, 32, 0.7)";
    return "transparent";
  }};
    border-radius: 7px 0 0 7px;
    transition: all ease 0.2s;
  }
`;

var MenuItemInner = styled.div`
  display: table-cell;
  vertical-align: middle;
  text-align: right;
  white-space: nowrap;
`;

var MenuItemContent = styled.div`
  display: inline;
  white-space: nowrap;
`;

var MenuIcon = styled.img`
  width: 26px;
  height: 26px;
  display: inline-block;
  vertical-align: middle;
  margin-left: 12px;
  filter: brightness(0) invert(1);
  transition: filter 0.2s ease;
`;

var MenuIconMoftabakh = styled(MenuIcon)`
  filter: ${function (props) {
    return props.focused
      ? "brightness(0) invert(1)"
      : "brightness(0) saturate(100%) invert(49%) sepia(32%) saturate(789%) hue-rotate(348deg) brightness(92%) contrast(90%)";
  }};
`;

var MenuLabel = styled.span`
  display: inline;
  vertical-align: middle;
  white-space: nowrap;
  color: #fff;
`;

var MenuLabelMoftabakh = styled(MenuLabel)`
  color: ${function (props) { return props.focused ? "#fff" : "#C3763E"; }};
`;

// Account item box
var AccItemBox = styled.div`
  width: 75%;
  margin-right: auto;
  margin-left: auto;
  border-radius: 25px;
  height: 63px;
  background-color: ${function (props) { return props.focused ? "#2c2c2c" : "transparent"; }};
  border: none;
  box-sizing: border-box;
  margin-bottom: 16px;
  transition: all ease 0.2s;
  color: ${function (props) { return props.focused ? "#fff" : "#bfbfbf"; }};
  text-align: center;
  line-height: 63px;
  font-weight: 700;
  font-size: 20px;
  display: table;
  table-layout: fixed;
  padding: 0 2rem;
  cursor: pointer;
  font-family: "AnjomanMax", sans-serif;
`;

var AccItemInner = styled.div`
  display: table-cell;
  vertical-align: middle;
  text-align: center;
`;

// Menu wrapper
var MenuWrapper = styled.div`
  height: 100vh;
  background-color: #212121;
  padding-top: 37px;
`;

// Logo container
var LogoContainer = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

var LogoImage = styled.img`
  display: block;
  width: 98px;
  height: 113px;
  margin-left: auto;
  margin-right: auto;
`;

// Divider
var Divider = styled.div`
  width: 80%;
  height: 1px;
  background-color: #595959;
  margin: 16px auto;
`;

// Check if running on WebOS (LG TV has its own D-pad)
// Note: Tizen (Samsung) TVs still show the virtual D-pad
var isTV = function () {
  if (typeof window === "undefined") return false;
  var ua = navigator.userAgent.toLowerCase();
  return ua.includes("webos") || ua.includes("smarttv");
};

// D-pad controller - fixed position at bottom left
var DPadFixedWrapper = styled.div`
  position: fixed;
  bottom: 30px;
  left: 30px;
  z-index: 9999;
  display: ${function () { return isTV() ? "none" : "block"; }};
`;

var DPadContainer = styled.div`
  width: 160px;
  height: 160px;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(60, 60, 60, 0.9) 0%, rgba(40, 40, 40, 0.95) 100%);
  border: 2px solid rgba(255, 255, 255, 0.15);
  position: relative;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

// Direction buttons
var DPadButton = styled.button`
  position: absolute;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 50%;
  color: #BFBFBF;
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;
  margin: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
    color: #fff;
  }

  &:active {
    background: rgba(180, 28, 32, 0.4);
  }
`;

var DPadIcon = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: block;
  text-align: center;
  line-height: 38px;
  font-size: 18px;
  font-weight: 400;
`;

var DPadUp = styled(DPadButton)`
  top: 6px;
  left: 50%;
  margin-left: -20px;
`;

var DPadDown = styled(DPadButton)`
  bottom: 6px;
  left: 50%;
  margin-left: -20px;
`;

var DPadLeft = styled(DPadButton)`
  left: 6px;
  top: 50%;
  margin-top: -20px;
`;

var DPadRight = styled(DPadButton)`
  right: 6px;
  top: 50%;
  margin-top: -20px;
`;

var DPadCenter = styled.button`
  position: absolute;
  top: 50%;
  left: 50%;
  margin-top: -22px;
  margin-left: -22px;
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, rgba(106, 0, 0, 0.6) 0%, rgba(80, 0, 0, 0.7) 100%);
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-radius: 50%;
  color: #fff;
  cursor: pointer;
  transition: all 0.15s ease;
  padding: 0;

  &:hover {
    background: linear-gradient(135deg, rgba(140, 0, 0, 0.7) 0%, rgba(100, 0, 0, 0.8) 100%);
  }

  &:active {
    opacity: 0.9;
  }
`;

var DPadCenterText = styled.span`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: block;
  text-align: center;
  line-height: 40px;
  font-size: 13px;
  font-weight: 700;
  font-family: "AnjomanMax", sans-serif;
`;

// Simulate keypress
var simulateKeypress = function (keyCode) {
  var event = new KeyboardEvent("keydown", { keyCode: keyCode });
  window.dispatchEvent(event);
};

// Menu item component with icon
function MenuItem({ children, target, icon, isMoftabakh }) {
  var navigate = useNavigate();
  var pathname = window.location.pathname;
  var isNavigationDisabled = useSelector(selectDisableNavigation);

  var handleNavigation = function () {
    if (!isNavigationDisabled) {
      navigate(target);
    }
  };

  var focusableResult = useFocusable({
    onEnterPress: handleNavigation,
    focusable: !isNavigationDisabled,
  });

  var ref = focusableResult.ref;
  var focused = focusableResult.focused;
  var focusSelf = focusableResult.focusSelf;

  // Determine if this item is the active route
  var isActive = false;
  if (target === "/") {
    isActive = pathname === "/";
  } else if (target === "/categories") {
    // Categories menu should be active for /categories, /category/*, and /genre/*
    isActive = pathname === "/categories" ||
      pathname.startsWith("/category/") ||
      pathname.startsWith("/genre/");
  } else {
    isActive = pathname.startsWith(target);
  }

  useEffect(function () {
    if (pathname.includes(target) && target !== "/") focusSelf();
    else if (pathname === "/" && target === "/") focusSelf();
  }, [pathname]);

  var isFocused = focused && !isNavigationDisabled;
  var IconComponent = isMoftabakh ? MenuIconMoftabakh : MenuIcon;
  var LabelComponent = isMoftabakh ? MenuLabelMoftabakh : MenuLabel;

  return (
    <MenuItemBox
      ref={ref}
      focused={isFocused}
      isActive={isActive && !isFocused}
      onClick={handleNavigation}
      style={{
        opacity: isNavigationDisabled ? 0.5 : 1,
        pointerEvents: isNavigationDisabled ? "none" : "auto",
        cursor: isNavigationDisabled ? "not-allowed" : "pointer",
      }}
    >
      <MenuItemInner>
        <MenuItemContent>
          {icon && <IconComponent src={icon} alt="" focused={isFocused} />}
          <LabelComponent focused={isFocused}>{children}</LabelComponent>
        </MenuItemContent>
      </MenuItemInner>
    </MenuItemBox>
  );
}

// D-Pad Component (fixed position, rendered outside sidebar)
// Hidden on player screen (/watch and /trailer routes) - player has its own dpad
export function DPadController() {
  // Hide on player pages - the player has its own dpad
  if (typeof window !== 'undefined' && (window.location.pathname.startsWith('/watch') || window.location.pathname.startsWith('/trailer'))) {
    return null;
  }

  return (
    <DPadFixedWrapper>
      <DPadContainer>
        <DPadUp onClick={function () { simulateKeypress(38); }}>
          <DPadIcon>▲</DPadIcon>
        </DPadUp>
        <DPadRight onClick={function () { simulateKeypress(39); }}>
          <DPadIcon>▶</DPadIcon>
        </DPadRight>
        <DPadCenter onClick={function () { simulateKeypress(13); }}>
          <DPadCenterText>OK</DPadCenterText>
        </DPadCenter>
        <DPadLeft onClick={function () { simulateKeypress(37); }}>
          <DPadIcon>◀</DPadIcon>
        </DPadLeft>
        <DPadDown onClick={function () { simulateKeypress(40); }}>
          <DPadIcon>▼</DPadIcon>
        </DPadDown>
      </DPadContainer>
    </DPadFixedWrapper>
  );
}

// Main Menu export
export function Menu({ focusKey: focusKeyParam }) {
  var token = localStorage.getItem("webTvUserToken");
  var focusableResult = useFocusable({
    focusable: true,
    saveLastFocusedChild: true,
    trackChildren: true,
    autoRestoreFocus: true,
    focusKey: focusKeyParam,
  });

  var ref = focusableResult.ref;
  var focusSelf = focusableResult.focusSelf;
  var focusKey = focusableResult.focusKey;

  // Menu items matching Android TV sidebar
  var menuItems = [
    { label: "صفحه اصلی", url: "/", icon: "/icons/home.svg" },
    { label: "جستجو", url: "/search", icon: "/icons/search.svg" },
    { label: "ژانر و دسته بندی", url: "/categories", icon: "/icons/category.svg" },
    { label: "فیلم", url: "/movie", icon: "/icons/movie.svg" },
    { label: "سریال", url: "/series", icon: "/icons/series.svg" },
    { label: "موسیقی", url: "/music", icon: "/icons/song.svg" },
    { label: "آناوطن", url: "/anavatan", icon: "/icons/iran.svg" },
    { label: "مفته باخ", url: "/moftabakh", icon: "/icons/play.svg", isMoftabakh: true },
    { label: token ? "حساب کاربری" : "ورود / ثبت نام", url: token ? "/profile" : "/login", icon: "/icons/user.svg" },
  ];

  useEffect(function () {
    focusSelf();
  }, [focusSelf]);

  return (
    <FocusContext.Provider value={focusKey}>
      <MenuWrapper ref={ref}>
        <LogoContainer>
          <Link to="/">
            <LogoImage src="/images/logo.svg" alt="Manabakh" />
          </Link>
        </LogoContainer>

        {menuItems.map(function (item) {
          return (
            <MenuItem
              key={"side_" + item.url}
              target={item.url}
              icon={item.icon}
              isMoftabakh={item.isMoftabakh}
            >
              {item.label}
            </MenuItem>
          );
        })}
      </MenuWrapper>
    </FocusContext.Provider>
  );
}

// Legacy Sidebar component (not used but kept for exports)
var Sidebar = function () {
  return null;
};

export default Sidebar;
